/**
 * Memorial Keepsakes — Memorial Photo Link builder (Preview Mode) + live QR (Live Mode).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Copy, Download, ExternalLink, Eye, EyeOff, Frame, Loader2, Nfc, QrCode, Share2, Sparkles,
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getBackendBaseUrl } from '../../utils/apiBase';
import {
  formatPersonDates,
  getMemorialPublicUrl,
  isMemorialPublished,
  personDisplayName,
} from '../../utils/memorial';
import { memorialQrPublicUrl } from '../../utils/publicAppUrl';
import { isPaidArchiveMember, canPreviewMemorialKeepsake, canRequestMemorialKeepsake, canSaveMemorialProductRequest, hasTemporaryKeepsakeUpload } from '../../utils/memorialKeepsakes';
import {
  getProductFrameClass,
  saveInterestRequest,
  useMemorialPhotoLinkBuilder,
} from '../../utils/memorialPhotoLinkBuilder';
import {
  buildCreatePayload,
  createMemorialProductRequest,
  resolveMemorialProductRequestImage,
} from '../../utils/memorialProductRequests';
import { trackProductEvent } from '../../utils/productEvents';
import {
  MEMORIAL_KEEPSAKE_MAIN_PRODUCTS,
  MEMORIAL_KEEPSAKE_SQUARE_GIFT,
  MEMORIAL_KEEPSAKES_CEMETERY_NOTE,
  MEMORIAL_KEEPSAKES_DYNAMIC_LINK_NOTE,
  MEMORIAL_KEEPSAKES_FREE_NOTE,
  MEMORIAL_KEEPSAKES_NFC_QR_COPY,
  MEMORIAL_KEEPSAKES_PAID_BADGE,
  MEMORIAL_KEEPSAKES_SECTION_TITLE,
  MEMORIAL_KEEPSAKES_SUBTITLE,
  MEMORIAL_PHOTO_LINK_ACTIVATION_NOTE,
  MEMORIAL_PHOTO_LINK_BUILDER_NOTE,
  MEMORIAL_PHOTO_LINK_NAME,
  MEMORIAL_PHOTO_LINK_PREVIEW_GUIDANCE,
  MEMORIAL_PHOTO_LINK_REQUEST_SAVED,
  MEMORIAL_PHOTO_LINK_UPLOAD_SAVE_BLOCKED,
  MEMORIAL_PHOTO_LINK_UPLOAD_FAILED,
  MEMORIAL_PHOTO_LINK_UPLOAD_SUCCESS,
  MEMORIAL_PHOTO_LINK_SUPPORTING,
  MEMORIAL_PHOTO_LINK_TAGLINE,
} from '../../content/memorialKeepsakesContent';
import { MemorialKeepsakePreviewDialog } from './MemorialKeepsakePreviewDialog';
import { MemorialPhotoLinkBuilder } from './MemorialPhotoLinkBuilder';
import {
  MemorialPhotoLinkCheckoutReviewDialog,
} from './MemorialPhotoLinkRequestDialog';
import { ModeBadge } from './KeepsakeFrameMockup';
import { HelpContextLink } from '../help';
import { Badge } from '../ui/badge';

const API = getApiBase();
const BACKEND = getBackendBaseUrl();

function formatScanDate(iso) {
  if (!iso) return 'Never';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return 'Never';
  }
}

function KeepsakeProductCard({ product, onPreview, onRequest, paidMember }) {
  const frameClass = getProductFrameClass(product, 'portrait');
  return (
    <article
      className="flex flex-col rounded-xl border border-[hsl(38_14%_86%)]/70 bg-gradient-to-b from-white/95 to-[hsl(42_28%_98%)] p-5 shadow-[0_1px_0_hsl(28_25_23_/_0.04)]"
      data-testid={`memorial-keepsake-${product.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div
          className={`relative shrink-0 rounded-md border border-[hsl(38_36%_42%_/_0.3)] bg-[hsl(42_28%_96%)] p-1.5 shadow-inner ${frameClass} max-w-[120px]`}
          aria-hidden
        >
          <div className="flex h-full w-full items-center justify-center rounded-sm bg-[hsl(38_18%_92%)]/80">
            <Frame className="h-5 w-5 text-[hsl(38_36%_42%)]/50" strokeWidth={1.25} />
          </div>
        </div>
        <Badge
          variant="outline"
          className="shrink-0 border-[hsl(38_36%_42%_/_0.35)] bg-[hsl(42_28%_96%)] text-[10px] font-medium uppercase tracking-wide text-[#5c4a32]"
        >
          {MEMORIAL_KEEPSAKES_PAID_BADGE}
        </Badge>
      </div>

      <h4 className="mt-4 font-serif text-lg font-light text-[#1c1917]">{product.title}</h4>
      <div className="mt-1 flex flex-wrap gap-1">
        {product.recommended ? (
          <Badge className="rounded-full bg-[hsl(152_32%_20%)] text-[10px] text-[hsl(42_30%_96%)]">Recommended</Badge>
        ) : null}
        {product.largest ? (
          <Badge variant="outline" className="rounded-full text-[10px]">Largest available size</Badge>
        ) : null}
      </div>
      <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-[#8b7355]">
        {product.variantOf || MEMORIAL_PHOTO_LINK_NAME}
      </p>
      <p className="mt-1 text-[11px] font-medium uppercase tracking-wide text-[#8b7355]/80">{product.size}</p>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-[#5c534a]">{product.copy}</p>

      <ul className="mt-3 space-y-1 text-[11px] text-[#5c534a]/90">
        <li className="flex items-center gap-1.5">
          <Nfc className="h-3 w-3 text-[#8b7355]" aria-hidden />
          NFC/QR link included
        </li>
        <li className="flex items-center gap-1.5">
          <QrCode className="h-3 w-3 text-[#8b7355]" aria-hidden />
          Dynamic link via /q/:shortCode
        </li>
      </ul>

      <div className="mt-5 flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          className="rounded-full bg-[hsl(152_32%_20%)] text-[hsl(42_30%_96%)] hover:bg-[hsl(152_28%_24%)]"
          disabled={!canPreviewMemorialKeepsake()}
          onClick={() => onPreview(product)}
        >
          Preview keepsake
        </Button>
        <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => onRequest(product)} disabled={!paidMember}>
          Review keepsake
        </Button>
      </div>
    </article>
  );
}

export function MemorialKeepsakesPanel({
  memorial,
  publicUrl,
  personName,
  person,
  sectionId = 'memorial-keepsakes',
  onPublishMemorial,
  publishing = false,
}) {
  const { user } = useAuth();
  const paidMember = isPaidArchiveMember(user);
  const canRequest = canRequestMemorialKeepsake(user);
  const published = isMemorialPublished(memorial);
  const memorialLink = getMemorialPublicUrl(publicUrl, memorial?.slug);
  const name = personName || personDisplayName(person);
  const dates = formatPersonDates(person);
  const memorialTitle = memorial?.title?.trim() || `${name} — Memorial`;

  const {
    builder,
    product,
    frameStyle,
    layout,
    previewImageUrl,
    personPortraitUrl,
    imageStatus,
    orientation,
    imagePosition,
    imageEdits,
    setProductId,
    setFrameStyleId,
    setLayoutId,
    setStep,
    setOrientation,
    setImagePosition,
    resetImagePosition,
    setImageEdits,
    resetImageEdits,
    rotateImageEdit,
    fitImage,
    fillImage,
    usePortraitImage,
    chooseImageLater,
    setUploadedFile,
    uploadingKeepsake,
    mainProducts,
    squareGift,
  } = useMemorialPhotoLinkBuilder(memorial, person, BACKEND, { canUploadKeepsake: canRequest });

  const canSaveRequest = canSaveMemorialProductRequest(user, builder);
  const [qrInfo, setQrInfo] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const [acting, setActing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [savingRequest, setSavingRequest] = useState(false);

  const activeProduct = pendingProduct || product;

  const loadQrInfo = useCallback(async () => {
    if (!memorial?.id) return;
    setLoadingQr(true);
    try {
      const { data } = await axios.get(`${API}/memorials-api/${memorial.id}/qr-info`);
      setQrInfo(data);
    } catch {
      setQrInfo(null);
    } finally {
      setLoadingQr(false);
    }
  }, [memorial?.id]);

  useEffect(() => {
    if (memorial?.id) loadQrInfo();
  }, [memorial?.id, loadQrInfo, published]);

  useEffect(() => {
    if (!memorial?.id) return;
    trackProductEvent('memorial_photo_link_builder_opened', {
      personId: person?.id,
      pillar: 'memorial_bridge',
      metadata: { published },
    });
  }, [memorial?.id, person?.id, published]);

  const openPreview = (p) => {
    if (p?.id && p.id !== builder.productId) setProductId(p.id);
    setPendingProduct(null);
    setPreviewOpen(true);
    trackProductEvent('memorial_photo_link_previewed', {
      personId: person?.id,
      pillar: 'memorial_bridge',
      metadata: { product_id: p?.id, published },
    });
  };

  const openRequest = (p) => {
    if (p?.id && p.id !== builder.productId) setProductId(p.id);
    setPendingProduct(p || null);
    setRequestOpen(true);
  };

  const temporaryUpload = hasTemporaryKeepsakeUpload(builder);

  const humanizeError = (err) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    return null;
  };

  const handleUploadFile = async (file) => {
    try {
      const result = await setUploadedFile(file);
      if (result?.message) {
        toast.success(result.message || MEMORIAL_PHOTO_LINK_UPLOAD_SUCCESS);
      }
    } catch (err) {
      toast.error(humanizeError(err) || MEMORIAL_PHOTO_LINK_UPLOAD_FAILED);
    }
  };

  const saveRequest = async () => {
    if (!canSaveRequest || !memorial?.id || !person?.id || savingRequest) return;

    const image = resolveMemorialProductRequestImage(builder, personPortraitUrl);
    if (!image.ok) {
      toast.message(temporaryUpload ? MEMORIAL_PHOTO_LINK_UPLOAD_SAVE_BLOCKED : 'Please choose an image for this keepsake.');
      return;
    }

    const payload = buildCreatePayload({
      memorial,
      person,
      product: activeProduct,
      frameStyle,
      layout,
      orientation,
      imageSource: image.imageSource,
      selectedImageRef: image.selectedImageRef,
      imagePosition,
      imageEdits,
      uploadedKeepsake: image.uploadedKeepsake,
    });

    setSavingRequest(true);
    try {
      await createMemorialProductRequest(payload);
      saveInterestRequest(memorial.id, {
        productId: activeProduct?.id,
        productTitle: activeProduct?.title,
        frameStyleId: builder.frameStyleId,
        frameLabel: frameStyle?.label,
        orientation,
        imageStatus,
        imageEdits,
        published,
        persisted: true,
      });
      trackProductEvent('memorial_photo_link_request_saved', {
        personId: person?.id,
        pillar: 'memorial_bridge',
        metadata: { product_id: activeProduct?.id, published, persisted: true },
      });
      toast.success(MEMORIAL_PHOTO_LINK_REQUEST_SAVED);
      setRequestOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save keepsake request.');
    } finally {
      setSavingRequest(false);
    }
  };

  const copyText = async (text, label = 'Link copied.') => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error('Could not copy link.');
    }
  };

  const downloadQr = async () => {
    if (!memorial?.id || !published) return;
    try {
      const res = await axios.get(`${API}/memorials-api/${memorial.id}/qr`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `memorial-qr-${memorial.slug || memorial.id.slice(0, 8)}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('QR code downloaded.');
      await loadQrInfo();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Live QR is available after publishing.');
    }
  };

  const toggleQrStatus = async () => {
    if (!memorial?.id || !qrInfo || !published) return;
    const next = qrInfo.status === 'active' ? 'disabled' : 'active';
    setActing(true);
    try {
      const { data } = await axios.patch(`${API}/memorials-api/${memorial.id}/qr-status`, { status: next });
      setQrInfo(data);
      toast.success(next === 'active' ? 'Dynamic QR enabled.' : 'Dynamic QR disabled.');
    } catch {
      toast.error('Could not update QR status.');
    } finally {
      setActing(false);
    }
  };

  const redirectUrl = published ? qrInfo?.redirect_url || memorialQrPublicUrl(qrInfo?.short_code) : null;
  const qrActive = qrInfo?.status === 'active';

  const builderConfig = { frameStyle, layout, previewImageUrl, orientation, imagePosition, imageEdits };

  const backToEdit = () => {
    setRequestOpen(false);
    setStep(1);
  };

  return (
    <>
      <section
        id={sectionId}
        className="scroll-mt-24 rounded-2xl border border-[hsl(38_16%_82%)]/80 bg-[hsl(42_28%_99%)] px-6 py-6 sm:px-8"
        data-testid="memorial-keepsakes"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#8b7355]">Memorial Bridge · V1</p>
              <ModeBadge published={published} />
            </div>
            <h3 className="mt-1 font-serif text-2xl font-light text-[#1c1917]">{MEMORIAL_KEEPSAKES_SECTION_TITLE}</h3>
            <p className="mt-3 font-serif text-lg font-light text-[#1c1917]">{MEMORIAL_PHOTO_LINK_NAME}</p>
            <p className="mt-2 text-sm leading-relaxed text-[#5c534a]">{MEMORIAL_PHOTO_LINK_TAGLINE}</p>
            <p className="mt-2 text-sm text-[#5c534a]/90">{MEMORIAL_PHOTO_LINK_SUPPORTING}</p>
            <p className="mt-3 text-xs text-[#5c534a]/80">{MEMORIAL_KEEPSAKES_SUBTITLE}</p>
            <p className="mt-2">
              <HelpContextLink section="keepsakes">Keepsake request help</HelpContextLink>
            </p>
          </div>
          {!paidMember ? (
            <div className="rounded-xl border border-[hsl(38_14%_86%)] bg-white/70 px-4 py-3 text-sm">
              <p className="font-medium text-[#1c1917]">{MEMORIAL_KEEPSAKES_FREE_NOTE}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" className="rounded-full" asChild>
                  <Link to="/pricing">View plans</Link>
                </Button>
                <Button type="button" size="sm" variant="ghost" className="rounded-full" onClick={() => openPreview(product)}>
                  Preview example
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        {!published ? (
          <div
            className="mt-5 rounded-xl border border-[hsl(38_14%_86%)] bg-[hsl(42_28%_98%)] px-4 py-4 text-sm text-[#5c534a]"
            role="status"
            data-testid="keepsake-preview-mode-banner"
          >
            <p className="leading-relaxed">{MEMORIAL_PHOTO_LINK_PREVIEW_GUIDANCE}</p>
            <p className="mt-2 text-xs text-[#8b7355]">{MEMORIAL_PHOTO_LINK_ACTIVATION_NOTE}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {onPublishMemorial ? (
                <Button
                  type="button"
                  size="sm"
                  className="rounded-full"
                  disabled={publishing}
                  onClick={onPublishMemorial}
                >
                  {publishing ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Share2 className="mr-1 h-3 w-3" />}
                  Publish memorial
                </Button>
              ) : null}
              <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => openPreview(product)}>
                Continue preview
              </Button>
            </div>
          </div>
        ) : null}

        <MemorialPhotoLinkBuilder
          published={published}
          builderState={builder}
          product={product}
          frameStyle={frameStyle}
          layout={layout}
          previewImageUrl={previewImageUrl}
          personPortraitUrl={personPortraitUrl}
          imageStatus={imageStatus}
          orientation={orientation}
          imagePosition={imagePosition}
          imageEdits={imageEdits}
          mainProducts={mainProducts}
          squareGift={squareGift}
          name={name}
          dates={dates}
          memorialTitle={memorialTitle}
          onProductChange={setProductId}
          onFrameChange={setFrameStyleId}
          onLayoutChange={setLayoutId}
          onStepChange={setStep}
          onOrientationChange={(value) => setOrientation(value, true)}
          onImagePositionChange={setImagePosition}
          onResetImagePosition={resetImagePosition}
          onImageEditsChange={setImageEdits}
          onResetImageEdits={resetImageEdits}
          onRotateImageLeft={() => rotateImageEdit('left')}
          onRotateImageRight={() => rotateImageEdit('right')}
          onFitImage={fitImage}
          onFillImage={fillImage}
          onUsePortrait={usePortraitImage}
          onChooseLater={chooseImageLater}
          onUploadFile={handleUploadFile}
          canUploadKeepsake={canRequest}
          uploadingKeepsake={uploadingKeepsake}
          onOpenPreview={() => openPreview(product)}
          onOpenCheckoutReview={() => openRequest(product)}
        />

        <div className="mt-6 rounded-xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_98%)]/80 px-4 py-4">
          <p className="text-sm leading-relaxed text-[#5c534a]">{MEMORIAL_KEEPSAKES_NFC_QR_COPY}</p>
          <p className="mt-2 text-xs text-[#5c534a]/85">{MEMORIAL_KEEPSAKES_DYNAMIC_LINK_NOTE}</p>
        </div>

        <div className="mt-6 rounded-xl border border-[hsl(38_14%_86%)]/60 bg-white/80 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="flex items-center gap-2 text-sm font-medium text-[#1c1917]">
                <Sparkles className="h-4 w-4 text-[#8b7355]" aria-hidden />
                {published ? 'Live dynamic link' : 'Dynamic link (activation pending)'}
              </p>
              <p className="mt-1 text-xs text-[#5c534a]">
                NFC and QR use <code className="rounded bg-[hsl(38_18%_92%)] px-1">/q/:shortCode</code> — not the raw
                memorial slug.
              </p>
            </div>
            {loadingQr ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
          </div>

          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Short link</dt>
              <dd className="mt-1 break-all font-mono text-xs text-foreground/80">
                {published && redirectUrl ? redirectUrl : 'Activation pending — design in Preview Mode'}
              </dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Destination</dt>
              <dd className="mt-1 break-all text-xs text-foreground/80">
                {published ? qrInfo?.destination_url || memorialLink || '—' : 'Assigned when published'}
              </dd>
            </div>
            {published ? (
              <>
                <div>
                  <dt className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Scans</dt>
                  <dd className="mt-1 text-foreground/85">{qrInfo?.scans_count ?? 0}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Last scanned</dt>
                  <dd className="mt-1 text-foreground/85">{formatScanDate(qrInfo?.last_scanned_at)}</dd>
                </div>
              </>
            ) : null}
          </dl>

          {published ? (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={downloadQr}>
                <Download className="mr-1 h-3 w-3" />
                Download QR PNG
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-full"
                disabled={!redirectUrl}
                onClick={() => copyText(redirectUrl, 'Dynamic link copied.')}
              >
                <Copy className="mr-1 h-3 w-3" />
                Copy short link
              </Button>
              {memorialLink ? (
                <Button type="button" size="sm" variant="ghost" className="rounded-full" asChild>
                  <a href={memorialLink} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-1 h-3 w-3" />
                    Preview memorial page
                  </a>
                </Button>
              ) : null}
              {qrInfo ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="rounded-full"
                  disabled={acting}
                  onClick={toggleQrStatus}
                >
                  {qrActive ? <EyeOff className="mr-1 h-3 w-3" /> : <Eye className="mr-1 h-3 w-3" />}
                  {qrActive ? 'Disable QR' : 'Enable QR'}
                </Button>
              ) : null}
            </div>
          ) : (
            <p className="mt-4 text-xs text-muted-foreground">{MEMORIAL_PHOTO_LINK_BUILDER_NOTE}</p>
          )}
        </div>

        <p className="mt-8 text-xs font-medium text-[#8b7355]">Memorial Photo Link sizes (A6 · A5 · A4)</p>
        <div className="mt-3 grid gap-4 lg:grid-cols-3">
          {MEMORIAL_KEEPSAKE_MAIN_PRODUCTS.map((p) => (
            <KeepsakeProductCard key={p.id} product={p} onPreview={openPreview} onRequest={openRequest} paidMember={canRequest} />
          ))}
        </div>
        <p className="mt-4 text-[11px] text-muted-foreground">Optional: {MEMORIAL_KEEPSAKE_SQUARE_GIFT.title} — preview only, not part of main ordering.</p>

        <p className="mt-6 text-[11px] leading-relaxed text-muted-foreground/80">{MEMORIAL_KEEPSAKES_CEMETERY_NOTE}</p>
      </section>

      <MemorialKeepsakePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        product={activeProduct}
        person={person}
        memorial={memorial}
        publicUrl={publicUrl}
        qrInfo={qrInfo}
        builderConfig={builderConfig}
      />

      <MemorialPhotoLinkCheckoutReviewDialog
        open={requestOpen}
        onOpenChange={setRequestOpen}
        product={activeProduct}
        frameStyle={frameStyle}
        imageStatus={imageStatus}
        orientation={orientation}
        published={published}
        personName={name}
        memorialTitle={memorialTitle}
        paidMember={canSaveRequest}
        saving={savingRequest}
        onSave={saveRequest}
        onBackToEdit={backToEdit}
      />
    </>
  );
}

export default MemorialKeepsakesPanel;
